import React, { FunctionComponent } from 'react';

import Flex from '@gojob/ui-bits/Flex';
import { graphql } from 'gatsby';
import { I18nextProvider } from 'react-i18next';

import { Section as SectionType } from '@gojob/cms';
import Footer from '../components/Footer';
import Header from '../components/Header';
import Section from '../components/Section';
import Seo from '../components/Seo';
import HrefLang, { LangRef } from '../components/Seo/HrefLang';
import { FooterFragmentResponse } from '../fragments/footer';
import { MetadataFragmentResponse } from '../fragments/metadata-fragment';
import { SlugFragmentResponse } from '../fragments/slug';
import { UniverseFragmentResponse } from '../fragments/universe';
import useTrackPageview from '../tracking/useTrackPageview';
import i18n from '../translations/i18n';

export type PageQueryResponse = {
  i18nRefs: {
    nodes: LangRef[];
  };
  sanitySiteMetadata?: {
    metadata?: MetadataFragmentResponse;
  };
  sanityFooter?: FooterFragmentResponse;
  sanityPage: {
    i18n_lang?: string;
    short_name?: string;
    universe: UniverseFragmentResponse;
    slug?: SlugFragmentResponse;
    sections?: SectionType[];
    metadata?: MetadataFragmentResponse;
  };
};

export type PageProps = {
  data: PageQueryResponse;
};

const Page: FunctionComponent<PageProps> = (props) => {
  const { data } = props;

  const siteMetadata = data.sanitySiteMetadata?.metadata || {};
  const { sections = [], metadata = {}, universe, slug, short_name, i18n_lang } = data.sanityPage;
  const sanityFooter = data.sanityFooter;
  const i18nRefs = data.i18nRefs.nodes;

  useTrackPageview(slug?.current);

  return (
    <>
      <I18nextProvider defaultNS={i18n_lang} i18n={i18n}>
        <HrefLang refs={i18nRefs} />
        <Seo {...siteMetadata} />
        <Seo i18n_lang={i18n_lang} {...metadata} />

        <Header universe={universe} />

        <Flex flexDirection="column">
          {sections.map((section) => {
            return <Section key={section._key} section={section} title={short_name} />;
          })}
        </Flex>

        {sanityFooter ? <Footer {...sanityFooter} /> : null}
      </I18nextProvider>
    </>
  );
};

export default Page;

export const query = graphql`
  query PageQuery($_id: String!, $i18n_lang: String, $i18nPagesGlob: String!) {
    i18nRefs: allSanityPage(filter: { _id: { glob: $i18nPagesGlob } }) {
      nodes {
        i18n_lang
        slug {
          ...SlugFragment
        }
      }
    }
    sanitySiteMetadata(i18n_lang: { eq: $i18n_lang }) {
      metadata {
        ...MetadataFragment
      }
    }
    sanityFooter(i18n_lang: { eq: $i18n_lang }) {
      ...FooterFragment
    }
    sanityPage(_id: { eq: $_id }) {
      short_name
      i18n_lang
      slug {
        ...SlugFragment
      }
      metadata {
        ...MetadataFragment
      }
      universe {
        ...UniverseFragment
      }
      sections {
        __typename
        ... on SanityEditoList {
          _key
          _type
          ...EditoListFragment
        }
        ... on SanitySellingPropositionList {
          _key
          _type
          ...SellingPropositionListFragment
        }
        ... on SanityLogoList {
          _key
          _type
          ...LogoListFragment
        }
        ... on SanityQuotes {
          _key
          _type
          ...QuoteCarouselFragment
        }
        ... on SanitySectors {
          _key
          _type
          ...SectorsFragment
        }
        ... on SanityHeroPic {
          _key
          _type
          ...HeroPicFragment
        }
        ... on SanityJobSearch {
          _key
          _type
          ...JobSearchFragment
        }
        ... on SanityEditoLinkList {
          _key
          _type
          ...EditoLinkListFragment
        }
        ... on SanityVignettes {
          _key
          _type
          ...VignetteCarouselFragment
        }
        ... on SanityBannerTitle {
          _key
          _type
          ...BannerTitleFragment
        }
        ... on SanityUserJourney {
          _key
          _type
          ...UserJourneyFragment
        }
        ... on SanityBreadcrumbs {
          _key
          _type
          ...BreadcrumbsFragment
        }
        ... on SanityFootNotes {
          _key
          _type
          ...FootNotesFragment
        }
        ... on SanityTsaSimulation {
          _key
          _type
          ...TsaSimulationFragment
        }
        ... on SanitySectionVideo {
          _key
          _type
          ...SectionVideoFragment
        }
        ... on SanityWaitinglist {
          _key
          _type
          ...WaitingListFragment
        }
      }
    }
  }
`;
